import React, { useState } from 'react';
import './SystemAdmin.css';

// System-wide settings managed by the iBetterAgent operator. Currently: the default
// trial length granted to a direct (System Org) self-signup. Org members provisioned
// by an org admin are governed by the org's own trial/seat terms, not this value.
function SystemSettings({ settings, onUpdate, onCleanData, impact = { mls: 0, orgs: 0, agents: 0 } }) {
  const [days, setDays] = useState(String(settings.signupTrialDays ?? 30));
  const [notice, setNotice] = useState('');
  const publicSignups = !!settings.publicSignups;
  const devMode = settings.devMode !== false;
  const [cleanArmed, setCleanArmed] = useState(false);
  const [cleanConfirm, setCleanConfirm] = useState('');

  function doClean() {
    if (cleanConfirm.trim().toUpperCase() !== 'DELETE') return;
    if (onCleanData) onCleanData();
    setCleanArmed(false); setCleanConfirm('');
    setNotice('All data below System was removed — every MLS, organization, agent, and their contacts/data.');
  }

  function toggleDevMode() {
    const next = !devMode;
    onUpdate({ devMode: next });
    setNotice(next
      ? 'Developer mode is ON — dev-only tools (e.g. the Contacts test-data generator) are visible.'
      : 'Developer mode is OFF — dev-only tools are hidden.');
  }

  function save() {
    const n = Math.max(0, parseInt(days, 10) || 0);
    onUpdate({ signupTrialDays: n });
    setDays(String(n));
    setNotice(n > 0 ? `New direct sign-ups now get a ${n}-day trial.` : 'New direct sign-ups now have no expiry until you set one.');
  }

  function togglePublicSignups() {
    const next = !publicSignups;
    onUpdate({ publicSignups: next });
    setNotice(next
      ? 'Public sign-ups are ON — anyone can create an account from the sign-up page.'
      : 'Public sign-ups are OFF — invite-only. Only people who open an invitation link can sign up.');
  }

  return (
    <div className="sa-container">
      <div className="sa-header">
        <div>
          <h2>Settings</h2>
          <p>Defaults for agents who sign up directly with iBetterAgent (System Org members).</p>
        </div>
      </div>

      {notice && <div className="sa-notice">{notice} <button className="sa-notice-x" onClick={() => setNotice('')}>✕</button></div>}

      <div className="sa-panel">
        <div className="sa-panel-title">🧪 Developer mode</div>
        <label className="sa-toggle-row">
          <input type="checkbox" checked={devMode} onChange={toggleDevMode} />
          <span>
            <strong>{devMode ? 'On — dev tools visible' : 'Off — dev tools hidden'}</strong>
            <span className="sa-toggle-badge" data-on={devMode}>{devMode ? 'DEV' : 'OFF'}</span>
          </span>
        </label>
        <p className="sa-mls-hint">
          Shows development-only helpers throughout the app (such as the Contacts <strong>🧪 +50 Test</strong> /
          <strong> Clear Test</strong> sample-data buttons). Turn this <strong>off</strong> for production.
        </p>
      </div>

      <div className="sa-panel">
        <div className="sa-panel-title">Public sign-ups</div>
        <label className="sa-toggle-row">
          <input type="checkbox" checked={publicSignups} onChange={togglePublicSignups} />
          <span>
            <strong>{publicSignups ? 'On — open to everyone' : 'Off — invite-only'}</strong>
            <span className="sa-toggle-badge" data-on={publicSignups}>{publicSignups ? 'OPEN' : 'INVITE-ONLY'}</span>
          </span>
        </label>
        <p className="sa-mls-hint">
          When <strong>off</strong>, the “Sign up” option is hidden on the landing/login page and new accounts are
          rejected — <strong>except</strong> people who open an invitation link from the Invite tab, who can still create
          an account. Turn this <strong>on</strong> for public launch so anyone can self-register.
        </p>
      </div>

      <div className="sa-panel">
        <div className="sa-panel-title">Direct sign-up default trial</div>
        <div className="sa-grid">
          <div className="sa-field">
            <label>Default trial (days)</label>
            <input type="number" min="0" value={days}
              onChange={e => { setDays(e.target.value); setNotice(''); }} />
          </div>
        </div>
        <p className="sa-mls-hint">
          When someone signs up directly (picking their MLS), their individual access is set to expire this many days
          out. Set <strong>0</strong> for no expiry. You can extend any member's expiration later from their System Org's Users tab.
          The System Org itself never expires.
        </p>
        <div className="sa-panel-actions">
          <button className="sa-add-btn" onClick={save}>Save</button>
        </div>
      </div>

      <div className="sa-panel" style={{ borderColor: '#fca5a5' }}>
        <div className="sa-panel-title" style={{ color: '#b91c1c' }}>🧹 Clean Data — danger zone</div>
        <p className="sa-mls-hint">
          Permanently removes <strong>everything below System</strong>: every MLS, every organization, every agent &amp;
          admin, and all of their contacts, properties, reminders, and files. Your System Admin login and the System-level
          reminders/settings are kept. <strong>This cannot be undone.</strong>
        </p>
        <p className="sa-mls-hint">
          Currently: <strong>{impact.mls}</strong> MLS, <strong>{impact.orgs}</strong> organization(s),{' '}
          <strong>{impact.agents}</strong> account(s) below System will be deleted.
        </p>
        {!cleanArmed ? (
          <div className="sa-panel-actions">
            <button className="sa-add-btn" style={{ background: '#b91c1c' }} onClick={() => { setCleanArmed(true); setCleanConfirm(''); setNotice(''); }}>
              🧹 Clean Data
            </button>
          </div>
        ) : (
          <>
            <div className="sa-field">
              <label>Type <code>DELETE</code> to confirm</label>
              <input value={cleanConfirm} placeholder="DELETE" autoFocus
                onChange={e => setCleanConfirm(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') doClean(); }} />
            </div>
            <div className="sa-panel-actions">
              <button className="sa-add-btn" style={{ background: '#b91c1c', opacity: cleanConfirm.trim().toUpperCase() === 'DELETE' ? 1 : 0.5 }}
                disabled={cleanConfirm.trim().toUpperCase() !== 'DELETE'} onClick={doClean}>
                Permanently delete everything below System
              </button>
              <button className="contacts-import-cancel-btn" onClick={() => { setCleanArmed(false); setCleanConfirm(''); }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SystemSettings;
